<template>
    <div>
        <div v-if="check.name" class="grid-parent-items-wrapper items-center gap-3 w-full pl-3 px-2">
            <font-awesome-icon icon="grip-vertical" class="text-base-content-300 text-gray-500" />
            <div class="grid-parent-items-inner justify-between transition-all duration-200 py-1 items-center gap-3 rounded-xl">
                <div class="w-full bg-white rounded-md">
                    <div class="w-full">
                        <div class="flex w-full gap-3 items-center justify-between text-sm p-2">
                            <!-- Input for the label when edited -->
                            <div class="flex justify-between w-3/12">
                                <input v-if="edited" v-model.trim="check.label" @click.stop="fieldNameClicked(check)"
                                    @blur="onUpdateFieldLabel(check)"
                                    :class="!check.label ? 'border border-dashed border-red-300 ' : ''" type="text"
                                    class="section_name w-48 bg-transparent border-0 outline-none p-1 text-md text-base-content focus:bg-gray-200 rounded focus:border-none border-b border-dashed border-gray-300 truncate" />
                                <span v-else v-tippy :content="fieldLabel"
                                    class="section_name bg-transparent w-48 border-0 outline-none p-1 text-md text-base-content font-semibold truncate">
                                    {{ fieldLabel }}
                                </span>
                                <!-- Edit button -->
                                <div v-if="this.$route.params.action !== 'view' && isEditPermission"
                                    class="h-8 w-8 flex items-center justify-center rounded-full hover:bg-dvbrandcolor hover:text-white-text transition-all duration-150"
                                    :class="edited ? 'bg-dvbrandcolor text-white-text' : 'text-primary'" @click.stop="onEdit(check)">
                                    <font-awesome-icon icon="edit" class="cursor-pointer text-sm fill-current"
                                        v-bind:style="{ opacity: this.$route.params.action !== 'view' ? 1 : 0.5, cursor: this.$route.params.action !== 'view' ? 'pointer' : 'not-allowed' }" />
                                </div>
                            </div>
                            <div class="w-3/12 flex justify-start">
                                <span v-tippy :content="getFieldName(check.name)">{{ getElementName }}</span>
                            </div>
                            
                            <!-- Entity information -->
                            <div class="flex gap-1 w-3/12">
                                <p class="optionHeading">Entity: </p>
                                <span class ="optionHeading" v-for="(entity,index) in check.entity_types" :key="entity.id">{{ entity.name }} <span v-if="index < check.entity_types.length - 1">, </span></span>
                            </div>
                            
                            <!-- Permissions button -->
                            <div class="flex justify-end w-2/12">
                                <!-- :disabled="!isEditMode" -->
                                <DropdownButton
                                    :title="$t('pages.workflow_engine.permissions')"
                                    @click="showGroupsModal"
                                />
                            </div>

                            <!-- Expand/Collapse and Remove button -->
                            <div class="flex items-center justify-end gap-3 w-1/12">
                                <span class="text-white-text text-sm flex items-center gap-2 cursor-pointer" @click="onExpanded(check)">
                                    <uparrow v-if="isExpanded" />
                                    <downarrow v-else />
                                </span>
                                <button class="cursor-pointer bg-gray-100 text-xs text-base-content-300  w-6 h-6 flex items-center justify-center rounded-full text-center" 
                                    :class="{ formListItem__disabled : check.disabled || disabled, 'hover:bg-primary-focus hover:text-white-text' : !isViewMode, 'opacity-50 cursor-not-allowed': isViewMode   }" @click="removeCheck(check)">
                                    <font-awesome-icon icon="times" class="fill-current text-current" />
                                </button>
                            </div>
                        </div>
                    </div>
                     <div :class="{'expanded': isExpanded, 'collapsed': !isExpanded}">
                        <!-- Combined description and monitor settings in one line -->
                        <div v-if="isExpanded" class="flex justify-between text-sm gap-2 bg-gray-50 p-3 rounded-md">
                            <!-- Description section -->
                            <div class="flex items-center bg-gray-100 p-2 rounded w-1/2">
                                <input v-if="editedDescription" v-model="check.description" @click.stop="fieldDescriptionClicked(check)"
                                        @blur="onUpdateFieldDescription(check)"
                                        :class="!check.description ? 'border border-dashed border-red-300 ' : ''" type="text"
                                        class="section_name w-full bg-transparent border-0 outline-none p-1 text-md text-base-content focus:bg-gray-200 rounded focus:border-none border-b border-dashed border-gray-300 truncate" />
                                <span v-else v-tippy :content="fieldDescription"
                                    class="section_name bg-transparent truncate w-full border-0 outline-none p-1 text-md text-base-content font-normal">
                                    Check Description: {{ fieldDescription }}
                                </span>
                                
                                <!-- Edit button for description -->
                                <div v-if="this.$route.params.action !== 'view' && isEditPermission"
                                    class="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-dvbrandcolor hover:text-white-text transition-all duration-150"
                                    :class="editedDescription ? 'bg-dvbrandcolor text-white-text' : 'text-primary'" @click.stop="onEditDescription(check)">
                                    <font-awesome-icon icon="edit" class="cursor-pointer text-sm fill-current"
                                        v-bind:style="{ opacity: this.$route.params.action !== 'view' ? 1 : 0.5, cursor: this.$route.params.action !== 'view' ? 'pointer' : 'not-allowed' }" />
                                </div>
                            </div>
                            
                            <!-- Monitor settings section - only show for specific checks -->
                            <div v-if="check.name.toLowerCase() !== 'vlex' && check.name.toLowerCase() !== 'poder' && !check.name.toLowerCase().includes('dnb') && check.category === 'Integrated Check'" 
                                class="flex items-center justify-between bg-gray-100 p-2 rounded w-1/2">
                                <div class="flex items-center">
                                    <div class="text-gray-600 mr-2">Monitoring Settings:</div>
                                    <neo-toggle-switch :status="monitoring_status" :disabled="editFrequency" @click="handleMonitoringStatus()" class="cursor-pointer" />
                                </div>
                                <div class="flex items-center">
                                    <label class="text-gray-500 text-sm mr-2">{{ $t('components.saas.frequency') }}</label>
                                    <select id="frequency" v-model="frequency" :disabled="!monitoring_status || editFrequency" 
                                        class="bg-white outline-none border border-solid border-gray-200 px-3 rounded-lg" 
                                        :class="!monitoring_status ? 'cursor-not-allowed': 'cursor-pointer'">
                                        <option selected disabled hidden value="null">{{ $t('components.saas.frequency') }}</option>
                                        <option v-for="(option,key) in frequencyList" :value="option" v-bind:key="key"> {{ option }} </option>
                                    </select>
                                </div>
                                <div v-if="this.$route.params.action !== 'view' && isEditPermission"
                                    class="h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-full hover:text-white-text transition-all duration-150"
                                    :class="edited ? 'bg-dvbrandcolor text-white-text' : 'text-primary'" >
                                    <font-awesome-icon icon="edit" v-if="editFrequency" class="text-primary text-xl cursor-pointer" @click.stop="onEditFrequecy()"
                                        v-bind:style="{ opacity: this.$route.params.action !== 'view' ? 1 : 0.5, cursor: this.$route.params.action !== 'view' ? 'pointer' : 'not-allowed' }" />
                                        <font-awesome-icon icon="save" v-else class="text-primary text-xl cursor-pointer"  @click.stop="onSave(check)"
                                        v-bind:style="{ opacity: this.$route.params.action !== 'view' ? 1 : 0.5, cursor: this.$route.params.action !== 'view' ? 'pointer' : 'not-allowed' }" />
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>
                <!-- Expanded content section -->
                
            </div>
        </div>

        <!-- Optionally, can be moved to the parent level -->
        <PermissionGroupController
            ref="permissions-controller"
            :disabled="!isEditMode"
            :groups="mappedPermissionGroups"
            :id="check.check_id"
            :selected="selectedPermissionGroups"
            :loading="checkPermissions.loading"
            @submit="onPermissionGroupsSubmit"
        />
    </div>
</template>

<script>
import uparrow from "@shared/assets/icon/svgs/uparrow.svg";
import downarrow from "@shared/assets/icon/svgs/downarrow.svg";
import { Dropdown as DropdownButton } from "@shared/components/buttons";
import PermissionGroupController from "@shared/components/PermissionGroupController";
import {
    getCheckPermissions,
    updateCheckPermissions,
} from "@/modules/checks/services.js";
import {
    getPermissionGroupsIdList,
    mapPermissionGroup
} from "@shared/components/PermissionGroupController/utils";
import { mapGetters, mapActions } from "vuex";
import NeoToggleSwitch from "@shared/components/toggle-switch";
import axios from "@/axios";
import { TOOL_TO_CHECK_NAME_MAP } from "@shared/tools/utils/constants"

export default {
    name: "fields",
    props: {
        currentTab: {
            type: String
        },
        showSidePanel: {
            type: Boolean,
            default: false,
        },
        activeFieldPanel: {
            type: Object,
            default: () => { },
        },
        check: {
            type: Object,
            default: () => { },
        },
        packageId: {
            type: String,
            required: true,
        },
        options: {
            type: Object,
            default: () => { },
        },
        section: {
            type: Object,
            default: () => { },
        },
        allReportsRoles: {
            type: Array,
            default: () => [],
        },
        fieldClass: {
            type: String,
            default: '',
        },
        updateFieldAttributesMethod: {
            type: Function,
        },
        showDeleteIcon: {
            type: Boolean,
            default: true
        },
        draggable: {
            type: Boolean,
            default: true
        },
        isEditPermission: {
            type: Boolean,
            default: true
        },
        inPackage: {
            type: Boolean,
        },
        isBlock: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: null,
        },
        packages: {
            type: Array,
            default: () => [],
        }
        // all permission groups
        // permissionGroups: {
        //     type: Object,
        //     default: () => ({
        //         client: [],
        //         tenant: [],
        //     })
        // }
    },
    components: {
        uparrow,
        downarrow,
        DropdownButton,
        PermissionGroupController,
        NeoToggleSwitch,
    },
    data() {
        return {
            frequencyList: [1, 7, 15, 30, 60, 90, 180, 365],
            frequency: null,
            edited: false,
            rolesLoading: false,
            newValue: '',
            oldValue: '',
            isExpanded: false,
            newValueDesc: '',
            oldValueDesc: '',
            editedDescription: false,
            oldCheckLabel: "",
            oldCheckDesc: "",
            monitoring_status:false,
            checkPermissions: {
                loading: false,
                data: {
                    client: [],
                    tenant: {},
                },
            },
            editFrequency: true,
            activeMonitoring:{},
            productMonitoring: null,
            productId: null,
        }
    },
    computed: {
        ...mapGetters(["getNeoCaseId", "getProduct", "getUserFeatures"]),
        isEditMode() {
            return this.$route.params.action === 'edit' && !!this.isEditPermission;
        },
        
        setFrequencyError() {
            return this.$t('monitoring.please_select_frequency_for_the_check')
        },
        getElementName() {
            let text = ''
            const max_length = 25;
            if (this.check.name && this.check.name.length > max_length) {
                text = this.check.name.substring(0, max_length) + ' ...'
                return text
            } else {
                return this.check.name
            }

        },
        fieldLabel() {
            return this.getFieldLabel(this.check);
        },
        fieldDescription()
        {
            return this.getFieldDescription(this.check);
        },
        // useblockvisible: {
        //     get() {
        //         return this.check.block_visibility
        //     },
        //     set(val) {
        //         this.check.block_visibility = val;
        //     },
        // }, 
        canDeleteField() {
            return !this.check.block_id
        },
        selectedPermissionGroups() {
            return getPermissionGroupsIdList(
                this.mappedPermissionGroups,
                {
                    filter: (el) => !!el.selected,
                    modifier: (el) => el.group_id,
                }
            );
        },
        
        mappedPermissionGroups() {
            const { tenant, clients } = this.checkPermissions.data;
            const tenantGroups = tenant?.groups || [];
            const clientGroups = clients || [];

                const mappedTenant = mapPermissionGroup(
                    tenantGroups,
                    {
                        modifier: (el) => ({
                            ...el,
                            id: el.group_id,
                        })
                    }
                )
                const mappedClient = mapPermissionGroup(
                    clientGroups,
                    {
                        modifier: (el) => ({
                            ...el,
                            client_id: el.name, // TODO check
                            client_name: el.name,
                        }),
                        listItemModifier: (el) => ({
                            ...el,
                            group_name: el.title,
                        })
                    }
                )
            return {
                tenant: mappedTenant,
                client: mappedClient,
            }
        },
        isViewMode() {
            return this.$route.params.action === 'view' || false;
        },
    },
    async mounted() {
    //     this.oldValue = this.check.label;
    //     this.oldValueDesc =  this.check.description;
    },
    methods: {
        ...mapActions({
              getDataMonitoring: "getDataMonitoring",
          }),
        fieldNameClicked(check) {
            this.oldValue = check.label?.toString().trim();
        },
        fieldDescriptionClicked(check) {
            this.oldValueDesc = check.description?.toString().trim();
        },
        getFieldLabel(field) {
            return field.label;
        },
        getFieldName(field) {
            return field.name;
        },
        removeCheck(check) {
            this.$emit('removeCheck', check);
        },
        getFieldDescription(field) {
            return field.description;
        },
        async onUpdateFieldLabel(check) {
            this.edited = false;
            if (!check.label) {
                this.check.label = this.oldCheckLabel;
                this.$toast.error("label cannot be empty");
                return;
            }
            else if(this.oldValue === check.label){
                return;
            } else {
                // this.check.label = this.oldCheckLabel;
                // check.label = this.newValue;
                this.$emit('updateFieldLabel', { check })
            }
        },
        onUpdateFieldDescription(check){
            this.editedDescription = false;
            if(this.oldValueDesc === check.description){
                return;
            } else {
                this.$emit('updateFieldLabel', { check })
            }
        },
       async onExpanded(check) {
            this.isExpanded = !this.isExpanded
            let data = this.getToolName(check)
            if(this.isExpanded)
            await this.getProductMonitoring(data)
            this.productMonitoring?.forEach(tool => {
                if(tool.tool === data.tool) {
                    this.monitoring_status = tool.status === "enabled" ? true : false;
                    this.frequency = tool.frequency
                }
            })
        },
        onEdit(check) {
            this.oldCheckLabel = check.label?.toString()?.trim();
            if (this.$route.params.action == 'view') {
                return
            }
            // if (!this.check.label) {
            //     this.$toast.error("Label cannot be empty");
            //     return;
            // }
            this.edited = !this.edited;
        },
        onEditFrequecy() {
            this.editFrequency = !this.editFrequency;
        },
         getIntegratedToolName(checkName) {
      const check = checkName?.toLowerCase()
      let toolName = ''

      for (const key in TOOL_TO_CHECK_NAME_MAP) {
        for (const checkName of TOOL_TO_CHECK_NAME_MAP[key]) {
          if (check?.includes(checkName)) {
            toolName = key;
            break;
          }
        }
      }

      return toolName;
    },
        getToolName(check) {
           let toolName =  this.getIntegratedToolName(check.name)
            
            // if(check.category === "Integrated Check") {
            //     this.productMonitoring.forEach(product => {
            //         if(product.tool === check.tool) {
            //             check.frequency = product.frequency
            //             check.status = product.status
            //             check._id = product._id
            //         }
            //     })
            // }

            check.tool = toolName
            return check
        },
       async  onSave(check) {
            if (!this.frequency) this.$toast.error(this.$t('monitoring.please_select_frequency_for_the_check'));
            else{
            this.editFrequency = true;
            if (check.tool=='company-name' && (this.getUserFeatures.includes("corporate_record_bvd__monitoring") || this.getUserFeatures.includes("corporate_record_op__monitoring"))) 
            {
                this.updateFrequency(check);
            }
           else if ( check.tool === "dnb" && this.getUserFeatures.includes("corporate_record_db__monitoring")) {
                this.updateFrequency(check);
            }
            else if (check.tool == 'lexis-bridger-tool' && this.getUserFeatures.includes("adverse_media_pep_sanctions__monitoring")) {
                this.updateFrequency(check);
            }
            else if (check.tool == 'orbis-tool' &&  this.getUserFeatures.includes("corporate_record_orbis__monitoring")) {
                this.updateFrequency(check);
            }
            else if (check.tool == 'world-compliance-tool' && this.getUserFeatures.includes("wco_data_file_monitoring__adverse_pep_sanction")) {
                this.updateFrequency(check);
            }
            else if(check.tool === "search-engine-tool" && this.getUserFeatures.includes("search_engine__monitoring")) {
                this.updateFrequency(check);
            }
            else {
                this.$toast.error("Changes cannot be implemented without a valid monitoring license");
                await this.getProductMonitoring(check);
                let product = this.productMonitoring.filter(tool => tool.tool === check.tool);
                if(product.length) {
                    this.monitoring_status = product[0].status === "enabled" ? true : false;
                    this.frequency = product[0].frequency
                } else {
                    this.monitoring_status = false;
                    this.frequency = null;
                }
            }
        }
           
        },

        updateFrequency(data) {
            this.activeMonitoring = data
            this.updateMonitoring(data)
        },
        handleSettings(event) {
            event.stopPropagation();
        },
        updateMonitoring() {
            this.activeMonitoring =  {
                        ...this.activeMonitoring,
                        ...{
                            status: this.monitoring_status,
                            frequency: this.frequency,
                        },
                    }
            
                    this.updateActiveMonitoring();
        },
                
        updateActiveMonitoring() {
            let checktool = this.productMonitoring?.find(product => product.tool == this.activeMonitoring.tool)
            let productId = this.packages?.find(product => product.id == this.packageId)
            if (checktool){
                this.activeMonitoring =  {
                ...this.activeMonitoring,
                ...{
                    id: checktool._id,
                },
            }
            this.updateDataMonitoring(this.activeMonitoring);
            } 
            else {
                 if(productId.product_id)this.createDataMonitoring(this.activeMonitoring);
                 else  this.$toast.error("IP sync error");
            }
        },

        // Function to set new monitoring
        async createDataMonitoring(monitoring) {
            let url = `/service/data-monitoring`;
            let freq = parseInt(monitoring.frequency, 10);
            let data = {
                frequency: freq,
                tool: monitoring.tool,
                monitoring_type: "product",
                product_name: this.productId.product_name,
                product_id: this.productId.product_id,
                status: monitoring.status ? 'enabled' : 'disabled',
            };
            try {
                let response = await axios.post(url, data);
                if ((response?.status === 201 || response?.status === 200) && response?.data.status) {
                    this.$toast.success("Monitoring set successfully !");
                    await this.getProductMonitoring(monitoring); // Function to fetch monitoring data
                }
            } catch (error) {
                console.error(error);
            }
        },
        

            async getProductMonitoring(check) {
                let productId= this.packages?.find(product => product.id == this.packageId)
                this.productId = productId;
                if(this.productId?.product_id) {
                let url = `/service/data-monitoring/product?product_id=${productId.product_id}&tool_name=${check.tool}`;
                try {
                    // let response = await axios.post(url, data);
                    let response = await axios.post(url);
                    if (response?.status === 200 && response?.data?.data) {
                        this.productMonitoring = response.data.data;
                    }
                } catch (error) {
                    console.error(error);
                }} else{
                    this.$toast.error("IP sync error");
                } 
            },
        
                // Function to update monitoring data
                async updateDataMonitoring(monitoring) {
                    let url = `/service/data-monitoring`;
                    let data = {
                        id: monitoring.id,
                        frequency: parseInt(monitoring.frequency, 10),
                        monitoring_type: "product",
                        status: monitoring.status? 'enabled':'disabled',
                    };
                    try {
                        let response = await axios.put(url, data);
                        if (response?.status === 200 && response?.data.status) {
                            this.$toast.success("Monitoring updated successfully !");
                            await this.getProductMonitoring(monitoring); // Function to fetch monitoring data
                            // this.close();
                        }
                    } catch (error) {
                        console.error(error);
                    }
                },
        onEditDescription(check){
            this.oldValueDesc = check.description?.toString()?.trim();
            if (this.$route.params.action == 'view') {
                return
            }
            this.editedDescription = !this.editedDescription;
        },

        handleMonitoringStatus() {
            this.monitoring_status = !this.monitoring_status;
            this.$forceUpdate();
        },

        async showGroupsModal() {
            // if (!this.isEditMode) return;

            this.checkPermissions.loading = true;
                this.$refs["permissions-controller"]?.showModal();
            try {
                const response = await getCheckPermissions({
                    checkId: this.check.check_id,
                    packageId: this.packageId,
                });

                this.checkPermissions.data = response;
            } catch (error) {
                console.error('Failed to fetch check permissions', error);
            }
            this.checkPermissions.loading = false;
        },

        isSelectedModifier(el, selected = this.selectedPermissionGroups) {
            return selected.includes(el.group_id)
        },

        /**
         * @param {String[]} selected selected group IDs
         */
        async onPermissionGroupsSubmit(selected) {
            this.checkPermissions.loading = true;
            try {
                const { tenant, clients } = this.checkPermissions.data;

                const payload = {
                    tenant: {
                        ...tenant,
                        groups: mapPermissionGroup(tenant.groups, {
                            modifier: (el) => ({
                                ...el,
                                selected: this.isSelectedModifier(el, selected),
                            }),
                        })
                    },
                    clients: mapPermissionGroup(clients, {
                        modifier: (el) => ({
                            ...el,
                            selected: this.isSelectedModifier(el, selected),
                        }),
                    })
                }

                await updateCheckPermissions({
                    checkId: this.check.check_id,
                    packageId: this.packageId,
                    data: payload,
                });

                // update stored data:
                this.checkPermissions.data = payload;
            } catch (error) {
                console.error("Failed to update check permissions:", error);
            }
            this.checkPermissions.loading = false;
            this.$refs["permissions-controller"]?.hideModal();
        },
    },
    // watch: {
    //     'check.label': function (newVal, oldVal) {
    //         this.newValue = newVal;
    //         this.oldValue =  oldVal
    //         console.log(oldVal, newVal);
    //     },
    //     'check.description': function (newVal, oldVal) {
    //         this.newValueDesc = newVal;
    //         this.oldValueDesc = oldVal;
    //         console.log(oldVal);
    //     },
    // },
};
</script>

<style lang="scss" scoped>

.block_visible {
    position: relative;
    align-self: center;
    padding-left: 10px;
}
.packageFieldRole{
    margin-left:1.8%;
}
.optionHeading{
    color: #0236bb;
    font-weight: 400;
}
.optionCircle{
    margin-top:5px;
}

.field-tags_container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
}
.block-role-panel-pad {
    padding-left: 18px;
}

.collect-data {
    max-width: 220px;
    min-width: 810px;
    width: 220px;  
    @apply bg-primary-100 border-primary-100;
    @apply h-7;
}

.grid-parent-items-wrapper {
  display: grid;
  grid-template-columns: 16px 1fr;
}

.grid-parent-items-inner {
  display: grid;
  grid-template-columns: 1fr;
}

.grid-child-items-wrapper {
  display: grid;
  grid-template-columns: 100px 1fr 50px;
}
.item {
  cursor: move;
  padding: 10px;
  background: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
}

/* Optional: You can add styling for the ghost class (the element while being dragged) */
.ghost {
  opacity: 0.5;
}
</style>
